import React, { useEffect } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAuth } from "../../../../providers/AuthProvider";
import { useAppStore } from "../../../../providers/AppStore";
import ActionButton from "../../../../common/ui/ActionButton";
import MoreOptionsPopover, { MenuOption } from "../common/MoreOptionsPopover";

/**
 * "+ Add results" split-dropdown on the Health overview toolbar (affiliate
 * only). Mirrors the Biomarkers tab's Import/Upload menu with the Health
 * overview copy: Upload Biomarkers / Sync InBody Data (locked with a
 * "Connect InBody" settings link when the affiliate has no InBody account
 * connected) / Sync EHR data (locked the same way until the affiliate has
 * stored a Keragon workflow in Settings).
 *
 * A locked option opens Settings in a NEW browser tab: this menu lives in the
 * client workspace tab, which the affiliate wants to keep. The Keragon list is
 * re-fetched every time the menu opens, so a workflow saved in that Settings
 * tab unlocks the option here on the next open without a reload.
 */
type Props = {
  onUpload: () => void;
  onSync: () => void;
  onSyncEhr: () => void;
};

// The shared PlusSvg has a hardcoded primary fill, so the white toolbar plus
// lives here.
const PlusIcon = ({ color }: { color: string }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 1v14M1 8h14"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const LockIcon = ({ color }: { color: string }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="4"
      y="10"
      width="16"
      height="10"
      rx="2"
      stroke={color}
      strokeWidth="2"
    />
    <path
      d="M8 10V7a4 4 0 0 1 8 0v3"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronToggle = ({ open, color }: { open: boolean; color: string }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transition: "transform 0.2s ease",
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
    }}
  >
    <path
      d="M6 9l6 6 6-6"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AddResultsMenu = ({ onUpload, onSync, onSyncEhr }: Props) => {
  const theme: Theme = useTheme();
  const { AuthUser } = useAuth();
  const { ASKeragonWorkflows, ASLoadKeragonWorkflows } = useAppStore();

  // Company-level and lazily loaded: this tab may be the first to need it.
  useEffect(() => {
    ASLoadKeragonWorkflows();
  }, [ASLoadKeragonWorkflows]);

  // Settings is affiliate-level and opens beside the client tab, not over it.
  const openSettingsTab = () => {
    window.open("/client/settings", "_blank", "noopener");
  };

  const isConnected = Boolean(AuthUser?.InBodyConnected);
  const hasEhrConnection = ASKeragonWorkflows.length > 0;
  const white = theme.colors_ds.NEUTRAL_GENERAL_WHITE;

  const options: MenuOption[] = [
    {
      label: "Upload Biomarkers",
      subtitle: "Upload a file (CSV, PDF).",
      onClick: onUpload,
    },
    isConnected
      ? {
          label: "Sync InBody Data",
          subtitle:
            "Automatically pull body composition scans directly from the InBody platform.",
          onClick: onSync,
        }
      : {
          label: "Sync InBody Data",
          // Clickable so the popover closes on click; the click opens Settings
          // in a new tab. Muted title + lock still read as "not available".
          titleMuted: true,
          titleIcon: <LockIcon color={theme.colorTokens.N[70]} />,
          onClick: openSettingsTab,
          subtitle: (
            <>
              <span style={styles.link(theme)}>Connect InBody</span> in Settings
              to sync client body composition data.
            </>
          ),
        },
    hasEhrConnection
      ? {
          label: "Sync EHR data",
          subtitle: "Pull lab results from a connected EHR system.",
          onClick: onSyncEhr,
        }
      : {
          label: "Sync EHR data",
          titleMuted: true,
          titleIcon: <LockIcon color={theme.colorTokens.N[70]} />,
          onClick: openSettingsTab,
          subtitle: (
            <>
              <span style={styles.link(theme)}>Connect a Keragon webhook</span>{" "}
              in Settings to pull EHR lab results.
            </>
          ),
        },
  ];

  return (
    <MoreOptionsPopover
      minWidth="300px"
      options={options}
      renderTrigger={(open, toggle) => (
        <ActionButton
          label="Add results"
          icon={<PlusIcon color={white} />}
          iconRight={<ChevronToggle open={open} color={white} />}
          onClick={(event) => {
            // Opening: refresh the workflow list so a connection saved in the
            // Settings tab unlocks "Sync EHR data" here without a reload.
            if (!open) ASLoadKeragonWorkflows({ force: true });
            toggle(event);
          }}
          sx={{
            backgroundColor: theme.colors_ds.PRIMARY_TEXT_MAIN,
            color: white,
            border: "none",
            "&:hover": { backgroundColor: theme.colorTokens.P[70] },
            "&:active": { backgroundColor: theme.colorTokens.P[90] },
          }}
        />
      )}
    />
  );
};

export default AddResultsMenu;

const styles = {
  link: (theme: Theme) => ({
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    cursor: "pointer",
  }),
};
